import React, { useState } from "react";
import {
  FiSearch,
  FiTrash2,
  FiRefreshCw,
  FiChevronLeft,
  FiChevronRight,
  FiAlertTriangle,
  FiFileText,
} from "react-icons/fi";
import {
  useGetAdminResultsQuery,
  useDeleteAdminResultMutation,
} from "../../redux/slices/api/adminApiSlice";
import { Bars } from "react-loader-spinner";
import { toast } from "react-toastify";

const AdminResults = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedResult, setSelectedResult] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { data, isLoading, error, refetch } = useGetAdminResultsQuery({
    page,
    limit: 20,
    search,
  });

  const [deleteResult, { isLoading: isDeleting }] =
    useDeleteAdminResultMutation();

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleDeleteClick = (result) => {
    setSelectedResult(result);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedResult) return;

    try {
      await deleteResult(selectedResult._id).unwrap();
      toast.success("Result deleted successfully");
      setShowDeleteModal(false);
      setSelectedResult(null);
    } catch (err) {
      toast.error(err?.data?.message || "Failed to delete result");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getCreatorName = (result) => {
    // Results use 'writers' array instead of 'creator'
    const writer = result.writers?.[0];
    if (!writer) return "N/A";
    if (writer.fundername) return writer.fundername;
    return `${writer.firstname || ""} ${writer.lastname || ""}`.trim() || "N/A";
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-600">Failed to load results. Please try again.</p>
        <button
          onClick={refetch}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary">Results</h1>
          <p className="text-gray-500">
            Manage all research results ({data?.pagination?.total || 0} total)
          </p>
        </div>
        <button
          onClick={refetch}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-light-primary transition-colors"
        >
          <FiRefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <form onSubmit={handleSearch}>
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by title or summary..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
            />
          </div>
        </form>
      </div>

      {/* Results Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Bars height="40" width="40" color="#8538FF" ariaLabel="loading" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">
                      Result
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">
                      Project
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">
                      Creator
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">
                      Created
                    </th>
                    <th className="text-right px-6 py-4 text-sm font-semibold text-gray-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data?.results?.map((result) => (
                    <tr key={result._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center flex-shrink-0">
                            <FiFileText className="text-yellow-600" size={18} />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-secondary truncate max-w-[250px]">
                              {result.title || "Untitled Result"}
                            </p>
                            <p className="text-sm text-gray-500 truncate max-w-[250px]">
                              {result.summary || "No summary"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-gray-600 truncate max-w-[200px] block">
                          {result.project?.title || "N/A"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                            {result.writers?.[0]?.images?.profile ? (
                              <img
                                src={result.writers[0].images.profile}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-primary text-xs font-semibold">
                                {getCreatorName(result)[0]?.toUpperCase() || "?"}
                              </span>
                            )}
                          </div>
                          <span className="text-gray-600 truncate max-w-[120px]">
                            {getCreatorName(result)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-gray-600 text-sm">
                          {formatDate(result.createdAt)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleDeleteClick(result)}
                            className="p-2 text-red-500 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete Result"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Empty State */}
            {data?.results?.length === 0 && (
              <div className="text-center py-12">
                <FiFileText className="mx-auto text-gray-300 mb-4" size={48} />
                <p className="text-gray-500">No results found</p>
              </div>
            )}

            {/* Pagination */}
            {data?.pagination && data.pagination.pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Showing{" "}
                  {(data.pagination.page - 1) * data.pagination.limit + 1} to{" "}
                  {Math.min(
                    data.pagination.page * data.pagination.limit,
                    data.pagination.total
                  )}{" "}
                  of {data.pagination.total} results
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronLeft size={16} />
                  </button>
                  <span className="px-3 py-1 text-sm">
                    Page {page} of {data.pagination.pages}
                  </span>
                  <button
                    onClick={() =>
                      setPage((p) => Math.min(data.pagination.pages, p + 1))
                    }
                    disabled={page === data.pagination.pages}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <FiAlertTriangle className="text-red-600" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-secondary">
                  Delete Result?
                </h3>
                <p className="text-sm text-gray-500 truncate max-w-[250px]">
                  {selectedResult?.title}
                </p>
              </div>
            </div>

            <p className="text-gray-600 mb-6">
              This action cannot be undone. The result will be permanently
              deleted.
            </p>

            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedResult(null);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isDeleting && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                Delete Result
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminResults;
